import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import Layout from '../../layout/Layout';
import { permissions } from '../../common/permissionGroup';
import AccessDenied from '../../components/AccessDenied/AccessDenied';
import PermissionsGate from '../../components/PermissionsGate/PermissionsGate';
import Card from '../../components/Card/Card';
import Table from '../../components/Table/Table';
import TableHead from '../../components/Table/TableHead';
import TableRow from '../../components/Table/TableRow';
import TableHeadData from '../../components/Table/TableHeadData';
import TableBody from '../../components/Table/TableBody';
import ButtonTypes from '../../constants/buttonTypes';
import TableData from '../../components/Table/TableData';
import Badge from '../../components/Badge/Badge';
import getEbayTasks from '../../thunks/ebay/getEbayTasks';
import EbayTasksStatus, { EbayTasksStatusColor } from '../../constants/ebayTasksStatus';
import CustomSelect from '../../components/Select/CustomSelect';
import { ebayAccountsList } from '../../constants/ebayAccounts';
import RequestEbayFile from './components/RequestEbayFile';
import ebayFeedTypes from '../../constants/ebayFeedTypes';

const summaryCountClassName = 'text-[14px] font-semibold';
const summarySuccessClassName = `${summaryCountClassName} text-success`;
const summaryFailureClassName = `${summaryCountClassName} text-danger`;

function EbayTasks() {
  const [account, setAccount] = useState(ebayAccountsList?.[0]);
  const [fType, setFeedType] = useState(ebayFeedTypes?.[0]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    getEbayTasks: {
      loading, data, total
    },
  } = useSelector((state) => ({
    getEbayTasks: state?.getEbayTasks,
  }), shallowEqual);

  const breadcrumbs = {
    pageTitle: 'eBay Tasks',
    history: [
      {
        title: 'Home',
        onClick: () => navigate('/')
      },
      {
        title: `eBay Tasks (${total || '-'})`,
      },
    ]
  };

  useEffect(() => {
    dispatch(getEbayTasks({
      account: account?.value,
      feedType: fType?.value
    }));
  }, [account, fType]);

  return (
    <Layout breadcrumbs={breadcrumbs} pageTitle="Ebay Tasks">
      <PermissionsGate
        can={permissions.PRINTER_READ}
        errorComponent={<AccessDenied />}
      >
        <Card
          showHeader
          footerBordered={false}
          headerLeft={(
            <div className="flex w-1/2 gap-[10px]">
              <div className="flex flex-1">
                <CustomSelect
                  value={[account]}
                  options={ebayAccountsList}
                  placeholder="Select account"
                  onChange={setAccount}
                />
              </div>
              <div className="flex flex-1">
                <CustomSelect
                  value={[fType]}
                  options={ebayFeedTypes}
                  placeholder="Select feed type"
                  onChange={setFeedType}
                />
              </div>
            </div>
          )}
        >
          <Table loading={loading}>
            <TableHead>
              <TableRow bordered>
                <TableHeadData />
                <TableHeadData>
                  id
                </TableHeadData>
                <TableHeadData>
                  status
                </TableHeadData>
                <TableHeadData>
                  type
                </TableHeadData>
                <TableHeadData>
                  creation date
                </TableHeadData>
                <TableHeadData>
                  completion date
                </TableHeadData>
                <TableHeadData>
                  summary
                </TableHeadData>
                <TableHeadData>
                  source file
                </TableHeadData>
                <TableHeadData>
                  result file
                </TableHeadData>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map(({
                taskId: id,
                status,
                feedType,
                creationDate,
                completionDate,
                uploadSummary
              }, index) => (
                <TableRow key={id} bordered={index < data.length - 1} dataRow={`${id}`}>
                  <TableData>{index + 1}</TableData>
                  <TableData>{id}</TableData>
                  <TableData>
                    <Badge
                      text={EbayTasksStatus[status]}
                      type={EbayTasksStatusColor[status]}
                    />
                  </TableData>
                  <TableData>{feedType}</TableData>
                  <TableData>
                    {creationDate && (
                      <Badge
                        text={DateTime.fromISO(creationDate, { zone: 'utc' }).toLocal().toFormat('yyyy-MM-dd HH:mm:ss')}
                        type={ButtonTypes.LIGHT}
                      />
                    )}
                  </TableData>
                  <TableData>
                    {completionDate && (
                      <Badge
                        text={DateTime.fromISO(completionDate, { zone: 'utc' })
                          .toLocal().toFormat('yyyy-MM-dd HH:mm:ss')}
                        type={ButtonTypes.LIGHT}
                      />
                    )}
                  </TableData>
                  <TableData>
                    <span className={summarySuccessClassName}>{uploadSummary?.successCount}</span>
                    /
                    {uploadSummary?.failureCount ? (
                      <span className={summaryFailureClassName}>{uploadSummary?.failureCount}</span>
                    ) : '-'}
                  </TableData>
                  <TableData>
                    <RequestEbayFile
                      taskId={id}
                      account={account?.value}
                      isResultFile={false}
                      btnText="Input File"
                    />
                  </TableData>
                  <TableData>
                    <RequestEbayFile taskId={id} account={account?.value} />
                  </TableData>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </PermissionsGate>
    </Layout>
  );
}

export default EbayTasks;
